import { createSlice, type PayloadAction, type Reducer } from '@reduxjs/toolkit'
import { DataSourceWithExtras } from '../services/common/common.types'
import { ScheduleDetails } from '../services/pipeline/pipeline.types'

export interface PickerOverlayData {
  nodeId?: string | null
  sourceType?: string | null
  sourceId?: string | number | null
}

export interface SourceConfigurationOverlayData extends DataSourceWithExtras {
  nodeId?: string | null
}

export interface SourceRegisterationOverlayData extends Partial<DataSourceWithExtras> {
  nodeId?: string | null
  sourceId?: string | number | null
  sourceType?: string | null
}

export interface SourceRegisteredTablesOverlayData {
  nodeId?: string | null
  sourceName?: string | null
  databaseName?: string | null
  schemaName?: string | null
  sourceDataSourceId?: number | null
  /** `roo_id` from the registered-source list response; required by the data-flow mapping payload. */
  rooId?: number | null
  /** Connector instance, e.g. `postgres`, `mysql`. Used as `type` for downstream API calls. */
  sourceInstance?: string | null
  /** `data_source_type` from the catalog (e.g. `DATABASE`, `FILE`). */
  dataSourceType?: string | null
  /** Pre-populated form values mapped from the registered source row (persisted to node session on Save). */
  registeredSourceValues?: Record<string, string>
  /** Catalog `data_source_id` — carried so Back can reopen the source picker with the same context. */
  pickerSourceId?: string | number | null
  /** Catalog `data_source_type` — carried so Back can reopen the source picker. */
  pickerSourceType?: string | null
}

export interface AddNewTableOverlayData {
  nodeId?: string | null
  sourceDataSourceId: number
  sourceInstance: string
  sourceName?: string | null
  dataSourceType?: string | null
  /** Mirrors the originating Table-List overlay payload so Back can restore it. */
  parentOverlay?: SourceRegisteredTablesOverlayData
}

export interface DestinationConfigurationOverlayData extends Partial<DataSourceWithExtras> {
  nodeId?: string | null
  sourceType?: string | null
  sourceId?: string | number | null
  isOpenedDirectly?: boolean
}

export interface DestinationRegisterationOverlayData extends Partial<DataSourceWithExtras> {
  nodeId?: string | null
  sourceId?: string | number | null
  sourceType?: string | null
}

export interface RegisteredDestinationsOverlayData {
  nodeId?: string | null
  destinationName?: string | null
  databaseName?: string | null
  schemaName?: string | null
  targetDataSourceId?: number | null
}

export interface SchedulePipelineOverlayData {
  pipelineName?: string | null
  dataFlowId?: number | null
  sessionId?: string | null
  scheduleDetails?: ScheduleDetails
  scheduleTypeName?: string | null
  /** True when the pipeline already has a schedule (e.g. canvas context). */
  isSchedule?: boolean
  /**
   * When true, saved schedule is written to pipeline Redux after success (canvas context).
   * List context should omit or set false so another pipeline’s schedule does not overwrite the store.
   */
  persistScheduleInPipelineStore?: boolean
}

export interface DeletePipelineOverlayData {
  pipelineId?: string | null
  pipelineName?: string | null
  source?: 'canvas' | 'pipeline-list' | null
}

export interface EditPipelineOverlayData {
  /** Optional override; defaults to the pipeline currently in Redux when null. */
  dataFlowId?: number | null
}

export interface DqReportOverlayData {
  pipelineId?: string | null
  pipelineName?: string | null
  passedRecords?: number | null
  failedRecords?: number | null
}

export interface DownloadOutputOverlayData {
  pipelineName?: string | null
  downloadFolderName?: string | null
  downloadTargetTableId?: number | null
}

export interface ExplorerDialogOverlayData {
  nodeId: string | null
  data_source_type?: string | null
}

export interface DataProfilingConfirmationOverlayData {
  nodeId: string | null
  // More data to be added later
}

export interface DataProfilingFieldEntry {
  source_file_field_id: number
  field_name: string
  field_data_type: string
}

export interface DataProfilingOverlayData {
  nodeId: string | null
  sessionId: string
  sourceFileId: number
  sourceName?: string | null
  tableName?: string | null
  fields: DataProfilingFieldEntry[]
}

export interface LoadTypeOverlayData {
  nodeId: string | null
  // More data to be added later
}

export interface OutputPreviewOverlayData {
  nodeId: string | null
  data_source_instance?: string | null
  data_source_type?: string | null
}

export interface ColumnMappingOverlayData {
  nodeId: string | null
  // More data to be added later
}

export interface TransformationSheetOverlayData {
  nodeId: string | null
  transformationId?: number | null
}

export interface JoinIntegrationSheetOverlayData {
  nodeId: string
  transformationId?: number | null
}

export interface TransformationDatasourcePickerOverlayData {
  nodeId?: string | null
}

export interface RunHistoryOverlayData {
  pipelineId: string
  pipelineName: string
  dataFlowId: number
}

export interface LogsOverlayData {
  pipelineName: string
  dataFlowId: number
  runId: string
  startTime?: string | null
  endTime?: string | null
}

type OverlayDataMap = {
  'schedule-pipeline-modal': SchedulePipelineOverlayData
  'delete-pipeline-dialog': DeletePipelineOverlayData
  'edit-pipeline-modal': EditPipelineOverlayData
  'source-picker': PickerOverlayData
  'source-registeration-modal': SourceRegisterationOverlayData
  'source-registered-tables-list': SourceRegisteredTablesOverlayData
  'source-add-new-table': AddNewTableOverlayData
  'source-configuration': SourceConfigurationOverlayData
  'destination-picker': PickerOverlayData
  'destination-registeration-modal': DestinationRegisterationOverlayData
  'registered-destinations-details': RegisteredDestinationsOverlayData
  'registered-destinations-table-list': RegisteredDestinationsOverlayData
  'destination-configuration': DestinationConfigurationOverlayData
  'dq-report-modal': DqReportOverlayData
  'download-output-modal': DownloadOutputOverlayData
  'explorer-dialog': ExplorerDialogOverlayData
  'data-profiling-confirmation': DataProfilingConfirmationOverlayData
  'data-profiling': DataProfilingOverlayData
  'load-type': LoadTypeOverlayData
  'output-preview': OutputPreviewOverlayData
  'column-mapping': ColumnMappingOverlayData
  'transformation-sheet': TransformationSheetOverlayData
  'join-integration-sheet': JoinIntegrationSheetOverlayData
  'transformation-datasource-picker': TransformationDatasourcePickerOverlayData
  'run-history-modal': RunHistoryOverlayData
  'logs-modal': LogsOverlayData
}

export type OverlayType = keyof OverlayDataMap

interface OverlayFrame {
  type: OverlayType
  data: OverlayDataMap[OverlayType] | null
}

export interface OverlayState {
  type: OverlayType | null
  isOpen: boolean
  data: OverlayDataMap[OverlayType] | null
  /**
   * Stack of overlays opened beneath the current one. When a second
   * `openOverlay` fires while one is already open, the existing frame is
   * pushed here so `popOverlay` (back-button style) can return to it.
   * `closeOverlay` does NOT consume this stack — it just closes the
   * current overlay; the stack is reset on the next fresh `openOverlay`.
   */
  stack: OverlayFrame[]
}

const initialState: OverlayState = {
  type: null,
  isOpen: false,
  data: null,
  stack: [],
}

const overlaySlice = createSlice({
  name: 'overlay',
  initialState,
  reducers: {
    openOverlay: <T extends OverlayType>(
      state: OverlayState,
      action: PayloadAction<{
        type: T
        data: OverlayDataMap[T]
      }>
    ) => {
      if (state.isOpen && state.type) {
        // Nested open: push the current frame so a back-button flow
        // (popOverlay) can return to it.
        state.stack.push({ type: state.type, data: state.data })
      } else {
        // Fresh open: discard any stale frames left from a previous session
        // so the stack never leaks across unrelated overlay openings.
        state.stack = []
      }
      state.isOpen = true
      state.type = action.payload.type
      state.data = action.payload.data
    },

    /**
     * Original behaviour: fully closes the overlay. Other modules rely on this
     * — do not change without auditing every `dispatch(closeOverlay())` call
     * site. Use `popOverlay` for the back-button semantics that nested
     * overlays (e.g. Logs → Run History) need.
     */
    closeOverlay: state => {
      state.isOpen = false
    },

    /**
     * Back-button style close: pops the previous overlay frame off the stack
     * and makes it the current overlay, or fully closes if the stack is
     * empty. Use this only from overlays that explicitly opt into the nested
     * push/pop flow.
     */
    popOverlay: state => {
      const previous = state.stack.pop()
      if (previous) {
        state.type = previous.type
        state.data = previous.data
        state.isOpen = true
      } else {
        state.isOpen = false
      }
    },

    clearOverlayData: state => {
      state.type = null
      state.data = null
    },
  },
})

export const { openOverlay, closeOverlay, popOverlay, clearOverlayData } = overlaySlice.actions

/** Generic `openOverlay` reducer above can widen inferred state to `{}`; pin state for `RootState`. */
const overlayReducer: Reducer<OverlayState> = overlaySlice.reducer
export default overlayReducer
